import type { LayoutDocument } from '../layout/types'

/**
 * Which printed slip goes with which service.
 *
 * The order — this machine's own slip, then the service's, then the branch's, then
 * the built-in one — is decided by the server (internal/api/layouts.go,
 * ticketAnswer). What arrives here is the slip this machine prints by default and
 * the services that print something else. So the page's whole rule is: the
 * service's entry if it has one, the default otherwise. A second copy of the order
 * here would be a second thing to drift.
 */

export interface SlipAnswer {
  document: LayoutDocument
  version: number
  layoutId?: string
  name?: string
}

export type SlipsByService = Record<string, SlipAnswer>

/** The design to send with a ticket for this service. */
export function slipFor(
  serviceId: string,
  fallback: LayoutDocument | null,
  byService: SlipsByService,
): LayoutDocument | null {
  return byService[serviceId]?.document ?? fallback
}

/** Every design this machine may print, each once, so their pictures can be
 *  fetched before the first customer rather than while one waits. */
export function slipDocuments(fallback: LayoutDocument | null, byService: SlipsByService): LayoutDocument[] {
  const out: LayoutDocument[] = fallback ? [fallback] : []
  const seen = new Set<string>()
  for (const slip of Object.values(byService)) {
    const id = slip.layoutId ?? ''
    if (id !== '' && seen.has(id)) continue
    seen.add(id)
    out.push(slip.document)
  }
  return out
}

/**
 * A fingerprint of what this machine prints, so that a thirty-second poll that
 * changed nothing does not hand the page new objects — and does not fetch every
 * logo again. Built from versions, which change on every publish.
 */
export function slipsKey(defaultVersion: number, byService: SlipsByService): string {
  const parts = Object.keys(byService)
    .sort()
    .map((id) => {
      const slip = byService[id]!
      return `${id}:${slip.layoutId ?? ''}:${slip.version}`
    })
  return [String(defaultVersion), ...parts].join(',')
}

/** The pictures one design draws, out of every picture fetched for this machine.
 *  Undefined rather than empty, so the field is left off the wire. */
export function picturesOf(
  document: LayoutDocument | null | undefined,
  all: Record<string, string>,
): Record<string, string> | undefined {
  if (!document) return undefined
  const out: Record<string, string> = {}
  for (const el of document.elements) {
    if ((el.kind !== 'image' && el.kind !== 'logo') || !el.assetId) continue
    const picture = all[el.assetId]
    if (picture) out[el.assetId] = picture
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/** The picture a slip's logo shows: its own, else the design's brand logo; none without a visible logo. */
export function slipLogo(document: LayoutDocument | null | undefined): string | undefined {
  const el = document?.elements.find((e) => e.kind === 'logo' && !e.hidden)
  if (!el) return undefined
  return el.assetId || document?.canvas.logo?.assetId || undefined
}
